import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from 'react';
import { BottomSheetContentLayout, MobileSummaryCard, TextField, Typography, useDevice, useOverlayContext } from 'seagull';
import { MobileTextFieldTID } from 'seagull/components/TextField/BaseTextField/BaseTextField.utils';
import { useTextFieldVariableIntegration } from '~/components/AgreementStudioPage/TemplateVariables/hooks/useTextFieldVariableIntegration';
import { VariableFieldAdornment } from '~/components/AgreementStudioPage/TemplateVariables/VariableFieldAdornment/VariableFieldAdornment';
import { useLocaleKeys } from '~/i18n';
import styles from './NotificationEditField.module.scss';
import { GetNotificationEditFieldTID } from './NotificationEditField.testIds';
const DesktopField = (param)=>{
    let { label, value, onChange, placeholder, error, disabled, multiline, minRows, bi, testId, adornmentClassName, allowedSystemKeys, disableCustomTags } = param;
    const { NotificationEditFieldTestIds: testIds } = useMemo(()=>GetNotificationEditFieldTID(testId), [
        testId
    ]);
    const inputRef = useRef(null);
    const integration = useTextFieldVariableIntegration(inputRef, onChange, {
        hideAdornment: disabled,
        allowedSystemKeys,
        disableCustomTags,
        testId
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(TextField, {
                label: label,
                value: value,
                onChange: (event)=>onChange(event.target.value),
                placeholder: placeholder,
                error: error,
                disabled: disabled,
                multiline: multiline,
                minRows: minRows,
                bi: bi,
                testId: testIds.Field(),
                inputRef: inputRef,
                inputProps: integration.inputProps,
                InputProps: disabled || !integration.enabled ? undefined : {
                    endAdornment: /*#__PURE__*/ _jsx("span", {
                        ref: integration.adornmentRef,
                        className: adornmentClassName,
                        children: /*#__PURE__*/ _jsx(VariableFieldAdornment, _object_spread({
                            testId: testIds.Adornment()
                        }, integration.fieldAdornmentProps))
                    })
                }
            }),
            integration.popovers
        ]
    });
};
const MobileField = (props)=>{
    const { pushBottomSheetPage } = useOverlayContext();
    const { label, value, error, testId } = props;
    const { NotificationEditFieldTestIds: testIds } = useMemo(()=>GetNotificationEditFieldTID(testId), [
        testId
    ]);
    return /*#__PURE__*/ _jsx(MobileSummaryCard, {
        testId: testIds.Field(),
        items: [
            {
                title: label,
                titleTestId: MobileTextFieldTID.Label(testIds.Field()),
                body: /*#__PURE__*/ _jsx(Typography, {
                    ellipsis: true,
                    testId: MobileTextFieldTID.Value(testIds.Field()),
                    skin: error ? 'error' : 'standard',
                    children: value
                }),
                actions: [
                    {
                        type: 'edit',
                        testId: MobileTextFieldTID.EditButton(testIds.Field()),
                        bi: {
                            name: 'edit_notification_field'
                        },
                        onClick: ()=>{
                            pushBottomSheetPage({
                                content: /*#__PURE__*/ _jsx(NotificationEditFieldBottomSheet, _object_spread({}, props))
                            });
                        }
                    }
                ]
            }
        ]
    });
};
const NotificationEditFieldBottomSheet = (param)=>{
    let { label, value, onChange, placeholder, error, disabled, multiline, minRows, bi, testId, adornmentClassName, allowedSystemKeys, disableCustomTags, validateInBottomSheet } = param;
    var _ref;
    const { t: contentCustomizationTexts } = useLocaleKeys('pages.notificationSettings.contentCustomization');
    const { hideBottomSheet } = useOverlayContext();
    const [tempValue, setTempValue] = useState(value);
    const inputRef = useRef(null);
    const { NotificationEditFieldTestIds: testIds } = useMemo(()=>GetNotificationEditFieldTID(testId), [
        testId
    ]);
    const integration = useTextFieldVariableIntegration(inputRef, setTempValue, {
        hideAdornment: disabled,
        allowedSystemKeys,
        disableCustomTags,
        testId
    });
    const validation = validateInBottomSheet === null || validateInBottomSheet === void 0 ? void 0 : validateInBottomSheet(tempValue);
    const handleSave = useCallback(()=>{
        if (tempValue !== value) onChange(tempValue);
        hideBottomSheet();
    }, [
        tempValue,
        value,
        onChange,
        hideBottomSheet
    ]);
    return /*#__PURE__*/ _jsxs(BottomSheetContentLayout, {
        contentClassName: styles.bottomSheetContent,
        footer: {
            primaryAction: {
                text: contentCustomizationTexts.saveButton(),
                onClick: handleSave,
                bi: {
                    name: 'save_notification_field_sheet'
                }
            }
        },
        children: [
            /*#__PURE__*/ _jsx(TextField, {
                collapseHelperText: true,
                label: label,
                value: tempValue,
                onChange: (event)=>setTempValue(event.target.value),
                placeholder: placeholder,
                multiline: multiline,
                minRows: minRows,
                error: (_ref = validation === null || validation === void 0 ? void 0 : validation.error) !== null && _ref !== void 0 ? _ref : error,
                helperText: validation === null || validation === void 0 ? void 0 : validation.helperText,
                bi: bi,
                testId: testIds.Field(),
                inputRef: inputRef,
                inputProps: integration.inputProps,
                InputProps: !disabled && integration.enabled ? {
                    endAdornment: /*#__PURE__*/ _jsx("span", {
                        ref: integration.adornmentRef,
                        className: adornmentClassName,
                        children: /*#__PURE__*/ _jsx(VariableFieldAdornment, _object_spread({
                            testId: testIds.Adornment()
                        }, integration.fieldAdornmentProps))
                    })
                } : undefined
            }),
            integration.popovers
        ]
    });
};
export const NotificationEditField = (props)=>{
    const { isMobileDevice } = useDevice();
    if (isMobileDevice && !props.disabled) return /*#__PURE__*/ _jsx(MobileField, _object_spread({}, props));
    return /*#__PURE__*/ _jsx(DesktopField, _object_spread({}, props));
};
